import {LoaderWithFetch} from "../components/Loader";
import React, {useContext, useEffect, useState} from "react";
import AnimatedButton from "../components/AnimatedButton";
import {PageContext} from "../PageContext";
import {useLocation, useNavigate} from "react-router-dom";
import {CheckoutPlanState} from "./CheckoutPlanPage";
import {NotifyPopup} from "../components/Popups";
import {scrollToId} from "../App";
import {Helmet} from "react-helmet-async";
import "./PlansPage.css"
import {PricingModel, PricingResponse} from "../types/pricing";


export enum ActionType {
    new = "new",
    renew = "renew",
    cancel = "cancel",
    upgrade = "upgrade",
    downgrade = "downgrade",
    login = "login",
    tos_not_accepted = "tos_not_accepted",
    none = "none",
}

interface Plan {
    id: number,
    credits: number,
    name: string,
    description: string,
    icon: string,
    price: number,
    actionType: ActionType,
    type: "byok_business" | string,
    active: boolean,
}

interface PlanData {
    plan: Plan
    creditsElement?: React.JSX.Element
}

interface CreditPurchase {
    id: number,
    credits: number,
    name: string,
    description: string,
    icon: string,
    price: number,
}

interface CreditPurchaseData {
    creditPurchase: CreditPurchase
}

interface Plans {
    plans: Plan[]
    creditPurchases: CreditPurchase[]
    credits: number
    activePlanId: number | null
    daysTillPlanRenewal?: number
    planRenewalDay?: number
    cancelled?: boolean
}

const BYOK_BUSINESS = "byok_business";

export default function PlansPage() {
    const location = useLocation()
    const [openErrorPopup, setOpenErrorPopup] = useState<boolean>(false)

    useEffect(() => {
        if (location.state?.error) {
            setOpenErrorPopup(true)
        }
    }, [location.state?.error]);

    return (
        <>
            <Helmet prioritizeSeoTags>
                <title>Pricing & Plans — Codebuddy AI Coding Assistant</title>
                <link rel="canonical" key="link-canonical" href="https://www.codebuddy.ca/plans"/>
                <meta property="description" name="description" key="description" content="Flexible pricing plans for Codebuddy AI coding assistant. Usage-based pricing: each request is charged in credits for the AI work it uses. BYOK (bring your own key) for $0, monthly subscriptions, and enterprise plans with on-site deployment."/>
                <meta property="og:title" name="og:title" key="og:title" content="Pricing & Plans — Codebuddy AI Coding Assistant"/>
                <meta property="og:description" name="og:description" key="og:description" content="Flexible pricing plans for Codebuddy AI coding assistant. Usage-based pricing, BYOK for $0, and enterprise plans."/>
                <meta property="og:url" name="og:url" key="og:url" content="https://www.codebuddy.ca/plans"/>
            </Helmet>
            <PlansTable includeUserData={true}/>
            <NotifyPopup open={openErrorPopup} setOpen={setOpenErrorPopup}>
                <div>
                    <h1>Error</h1>
                    <p>{location.state?.error}</p>
                </div>
            </NotifyPopup>
        </>
    )
}

export function PlansTable(props: { includeUserData: boolean }) {
    const [isTestMode, setIsTestMode] = useState<boolean>(false)
    const pageContext = useContext(PageContext)
    const isAdmin = pageContext.loggedIn && pageContext.admin
    const prefix = isTestMode ? "test/" : ""
    const navigate = useNavigate()

    return (
        <div className={"gradient__07_smooth full-page-gradient"}>
            <LoaderWithFetch<Plans> url={`/api/v1/${prefix}plans`}>
                {
                    (plans) => {
                        return (
                            <>
                                {
                                    isAdmin && (
                                        <div style={{position: "absolute", right: "100px", top: "100px"}}>
                                            <AnimatedButton onClick={() => setIsTestMode(!isTestMode)}>
                                                {isTestMode ? "Test Mode" : "Live Mode"}
                                            </AnimatedButton>
                                        </div>
                                    )
                                }
                                <div className="section">
                                    {
                                        props.includeUserData && (
                                            <PlanUserData plans={plans}/>
                                        )
                                    }
                                    <div className={"section"}>
                                        <PlanList plans={plans.plans} cancelled={plans.cancelled === true}
                                                  testMode={isTestMode}/>
                                    </div>
                                    {
                                        props.includeUserData && (
                                            <>
                                                {
                                                    (plans.activePlanId !== null || isAdmin) && (
                                                        <div className={"section"}>
                                                            <CreditPurchaseList creditPurchases={plans.creditPurchases}
                                                                                testMode={isTestMode}/>
                                                        </div>
                                                    )
                                                }
                                                <div className="section">
                                                    <div className={"container"}>
                                                        <AnimatedButton
                                                            onClick={() => window.location.href = `/${prefix}billing`}>
                                                            Payment History
                                                        </AnimatedButton>
                                                        <span style={{display: "inline-block", width: "12px"}}/>
                                                        <AnimatedButton onClick={() => navigate("/usage")}>
                                                            Usage
                                                        </AnimatedButton>
                                                    </div>
                                                </div>
                                            </>
                                        )
                                    }
                                    <HowDoCreditsWorkSection testMode={isTestMode}/>
                                </div>
                            </>
                        )
                    }
                }
            </LoaderWithFetch>
        </div>
    )
}

function PlanUserData(props: { plans: Plans }) {
    const plans = props.plans
    return (
        <>
            <div className="container-sm simple-grid">
                <div className="style-section-heading">
                    <div className="w-layout-grid row-md items-center items-justify-center">
                        <h4 className="font-title">Available Credits</h4>
                    </div>
                </div>
                <div className="text-lg _52ch">{plans.credits}</div>
            </div>
            {
                plans.activePlanId != null && !plans.cancelled && (
                    <>
                        <div className="container-sm simple-grid">
                            <div className="style-section-heading">
                                <div
                                    className="w-layout-grid row-md items-center items-justify-center">
                                    <h4 className="font-title">Days until plan renewal</h4>
                                </div>
                            </div>
                            <div className="text-lg _52ch">{plans.daysTillPlanRenewal}</div>
                        </div>
                        <div className="container-sm simple-grid">
                            <div className="style-section-heading">
                                <div
                                    className="w-layout-grid row-md items-center items-justify-center">
                                    <h4 className="font-title">Plan renews on the</h4>
                                </div>
                            </div>
                            <div className="text-lg _52ch">{plans.planRenewalDay}</div>
                        </div>
                    </>
                )
            }
            {
                plans.cancelled && (
                    <>
                        <div className="container-sm simple-grid">
                            <div className="style-section-heading">
                                <div
                                    className="w-layout-grid row-md items-center items-justify-center">
                                    <h4 className="font-title">The plan has been cancelled</h4>
                                </div>
                            </div>
                        </div>
                    </>
                )
            }
        </>
    )
}

function translateActionName(plan: Plan, cancelled: boolean) {
    let renewPrefix = cancelled ? "Renew & " : "";
    if (plan.type === BYOK_BUSINESS) {
        switch (plan.actionType) {
            case ActionType.upgrade:
            case ActionType.downgrade:
                return renewPrefix + "Select"
        }
    }
    switch (plan.actionType) {
        case ActionType.new:
        case ActionType.login:
        case ActionType.tos_not_accepted:
            return "Subscribe"
        case ActionType.renew:
            return "Renew"
        case ActionType.cancel:
            return "Cancel"
        case ActionType.upgrade:
            return renewPrefix + "Upgrade"
        case ActionType.downgrade:
            return renewPrefix + "Downgrade"
        default:
            return null
    }
}

function PlanEntry(props: { planData: PlanData, cancelled: boolean, testMode: boolean }) {
    const plan = props.planData.plan
    const actionName = translateActionName(plan, props.cancelled);
    const navigate = useNavigate()
    const context = useContext(PageContext)

    const executeAction = () => {
        if (plan.actionType === ActionType.login) {
            context.handleError("not_logged_in", "/plans")
            return
        }
        if (plan.actionType === ActionType.tos_not_accepted) {
            context.handleError("tos_not_accepted", "/plans")
            return
        }
        const checkoutPlanState: CheckoutPlanState = {
            planId: plan.id,
            testMode: props.testMode,
            actionType: plan.actionType
        }
        try {
            gtag('event', 'begin_checkout', {
                'currency': 'USD',
                'value': plan.price,
                'items': [{
                    'item_id': plan.id,
                    'item_name': plan.name,
                    'price': plan.price,
                    'category': plan.type,
                    'quantity': 1,
                    'item_variant': plan.credits,
                }]
            });
        } catch (e) {
            console.error(e)
        }
        navigate(`/checkoutPlan`, {state: checkoutPlanState})
    }
    const priceSection = (
        <>
            <div className="text-xs">$</div>
            <h3
                className="title-xs">{plan.price / 100}</h3>
            <div className="text-xs">USD</div>
            {
                plan.price > 0 && (
                    <div className="text-xs">/ month</div>
                )
            }
        </>
    )
    const descriptionSection = (
        <>
            {plan.description && (
                <div className="bonus-tag">{plan.description.replace(/(<([^>]+)>)/gi, "")}</div>
            )}
        </>
    )
    const actionSection = (
        <>
            {
                actionName && (
                    <AnimatedButton onClick={executeAction}>
                        {actionName}
                    </AnimatedButton>
                )
            }
        </>
    )
    const checkboxSections = [
        (
            <div className="text-sm">
                {plan.credits}
                {
                    props.planData.creditsElement ?? (
                        <>Credits<CreditInfo/> per month</>
                    )
                }
            </div>
        ),
        (
            <div className="text-sm">
                Leftover Credits transfer to the next month
            </div>
        ),
    ]
    return (
        <PurchaseEntryBase name={`${plan.name} Plan`} priceSection={priceSection}
                           descriptionSection={descriptionSection}
                           actionSection={actionSection} checkboxSections={checkboxSections}/>
    )
}

function PurchaseEntryBase(props: {
    name: string,
    priceSection: React.JSX.Element,
    descriptionSection: React.JSX.Element,
    actionSection: React.JSX.Element,
    checkboxSections: React.JSX.Element[]
}) {

    return (
        <div className="item-team-pricing rounded">
            <div className="header-team-pricing">
                <div className="margin-b-md">
                    <div className="w-layout-grid column-sm content-centered">
                        <div>
                            <h5 className="font-title">{props.name}</h5>
                        </div>
                        <div className="w-layout-grid row-xs items-center items-justify-center">
                            {props.priceSection}
                        </div>
                        {props.descriptionSection}
                    </div>
                    <div className="w-layout-grid row-xs items-center items-justify-center margin-md">
                        {props.actionSection}
                    </div>
                </div>
            </div>
            <div className="description-team-pricing">
                <div className="w-layout-grid list-plan-pricing">
                    {
                        props.checkboxSections.map((section, index) => (
                            <div key={index} className="w-layout-grid row-xs">
                                <div className="material-icons-round">
                                    <div className="text-sm">check</div>
                                </div>
                                {section}
                            </div>
                        ))
                    }
                </div>
            </div>
        </div>
    );
}

function CreditPurchaseEntry(props: { creditPurchaseData: CreditPurchaseData, testMode: boolean }) {
    const creditPurchase = props.creditPurchaseData.creditPurchase
    const navigate = useNavigate()

    const executeAction = () => {
        try {
            gtag('event', 'begin_checkout', {
                'currency': 'USD',
                'value': creditPurchase.price,
                'items': [{
                    'item_id': creditPurchase.id,
                    'item_name': creditPurchase.name,
                    'price': creditPurchase.price,
                    'category': "credit_purchase",
                    'quantity': 1,
                    'item_variant': creditPurchase.credits,
                }]
            });
        } catch (e) {
            console.error(e)
        }
        navigate(`/checkoutCreditPurchase`, {state: {creditPurchaseId: creditPurchase.id, testMode: props.testMode}})
    }
    const priceSection = (
        <>
            <div className="text-xs">$</div>
            <h3
                className="title-xs">{creditPurchase.price / 100}</h3>
            <div className="text-xs">USD</div>
        </>
    )
    const descriptionSection = (
        <>
            {creditPurchase.description && (
                <div className="bonus-tag">{creditPurchase.description.replace(/(<([^>]+)>)/gi, "")}</div>
            )}
        </>
    )
    const actionSection = (
        <AnimatedButton onClick={executeAction}>
            Buy
        </AnimatedButton>
    )
    const checkboxSections = [
        (
            <div className="text-sm">
                {creditPurchase.credits} Credits
            </div>
        ),
        (
            <div className="text-sm">
                Leftover Credits transfer to the next month
            </div>
        ),
    ]
    return (
        <PurchaseEntryBase name={creditPurchase.name} priceSection={priceSection}
                           descriptionSection={descriptionSection}
                           actionSection={actionSection} checkboxSections={checkboxSections}/>
    )
}

function FreeTrialPlanEntry() {

    const priceSection = (
        <>
            <div className="text-xs">$</div>
            <h3
                className="title-xs">0</h3>
            <div className="text-xs">USD</div>
        </>
    )

    const checkboxSections = [
        (
            <div className="text-sm">
                300 Credits on signup
            </div>
        ),
        (
            <div className="text-sm">
                Leftover Credits transfer to the next month
            </div>
        ),
    ]

    return (
        <PurchaseEntryBase
            name="Free Trial"
            priceSection={priceSection}
            descriptionSection={<></>}
            actionSection={(<></>)}
            checkboxSections={checkboxSections}/>
    )
}

function BYOKPersonalPlanEntry() {
    const priceSection = (
        <>
            <div className="text-xs">$</div>
            <h3
                className="title-xs">0</h3>
            <div className="text-xs">USD</div>
        </>
    )

    const checkboxSections = [
        (
            <div className="text-sm">
                Bring your own OpenAI Key
            </div>
        ),
        (
            <div className="text-sm">
                Free for personal projects
            </div>
        ),
        (
            <div className="text-sm">
                Add an Anthropic or OpenRouter key to cover Claude, Gemini and the rest — anything your keys
                don't cover is billed with credits
            </div>
        ),
    ]

    const navigate = useNavigate()
    const context = useContext(PageContext)

    const executeAction = () => {
        if (context.loggedIn) {
            navigate("/profile", {state: {scrollTo: {targetId: "set-api-key", targetPath: "/profile"}}})
        } else {
            context.handleError("not_logged_in", "/profile#set-api-key")
        }
    }

    const actionSection = (
        <AnimatedButton onClick={executeAction}>
            Set Key
        </AnimatedButton>
    )

    return (
        <PurchaseEntryBase
            name="BYOK Personal"
            priceSection={priceSection}
            descriptionSection={<></>}
            actionSection={(actionSection)}
            checkboxSections={checkboxSections}/>
    )
}

function BYOKBusinessPlanEntry(props: { plan: Plan, testMode: boolean, cancelled: boolean }) {
    const context = useContext(PageContext)
    const navigate = useNavigate()
    const plan = props.plan
    const actionName = translateActionName(plan, props.cancelled);

    const executeAction = () => {
        if (plan.actionType === ActionType.login) {
            context.handleError("not_logged_in", "/plans")
            return
        }
        if (plan.actionType === ActionType.tos_not_accepted) {
            context.handleError("tos_not_accepted", "/plans")
            return
        }
        const checkoutPlanState: CheckoutPlanState = {
            planId: plan.id,
            testMode: props.testMode,
            actionType: plan.actionType
        }
        navigate(`/checkoutPlan`, {state: checkoutPlanState})
    }
    const priceSection = (
        <>
            <div className="text-xs">$</div>
            <h3
                className="title-xs">{plan.price / 100}</h3>
            <div className="text-xs">USD</div>
            {
                plan.price > 0 && (
                    <div className="text-xs">/ month</div>
                )
            }
        </>
    )
    const descriptionSection = (
        <>
            {plan.description && (
                <div className="bonus-tag">{plan.description.replace(/(<([^>]+)>)/gi, "")}</div>
            )}
        </>
    )
    const actionSection = (
        <>
            {
                actionName && (
                    <AnimatedButton onClick={executeAction}>
                        {actionName}
                    </AnimatedButton>
                )
            }
        </>
    )
    const checkboxSections = [
        (
            <div className="text-sm">
                Bring your own OpenAI Key
            </div>
        ),
        (
            <div className="text-sm">
                For commercial projects
            </div>
        ),
        (
            <div className="text-sm">
                Add an Anthropic or OpenRouter key to cover Claude, Gemini and the rest — anything your keys
                don't cover is billed with credits
            </div>
        ),
    ]

    return (
        <PurchaseEntryBase name={`${plan.name} Plan`} priceSection={priceSection}
                           descriptionSection={descriptionSection}
                           actionSection={actionSection} checkboxSections={checkboxSections}/>
    )
}

function EnterprisePlanEntry() {
    const navigate = useNavigate()
    const executeAction = () => {
        navigate(`/contact`)
    }

    const priceSection = (
        <>
            <h3 className="title-xs">-</h3>
        </>
    )

    const checkboxSections = [
        (
            <div className="text-sm">
                Contact us for custom pricing
            </div>
        ),
        (
            <div className="text-sm">
                Custom installation on-site or in the cloud for added code privacy
            </div>
        ),
        (
            <div className="text-sm">
                Priority Support
            </div>
        ),
    ]

    const actionSection = (
        <>
            <AnimatedButton onClick={executeAction}>
                Contact Us
            </AnimatedButton>
        </>
    )

    return (
        <PurchaseEntryBase
            name="Enterprise Plan"
            priceSection={priceSection}
            descriptionSection={<>For organizations</>}
            actionSection={actionSection}
            checkboxSections={checkboxSections}/>
    )
}

function PlanList(props: { plans: Plan[], cancelled: boolean, testMode: boolean }) {

    const normalPlans = props.plans.filter((plan) => {
        return plan.type !== BYOK_BUSINESS
    })
    const byokBusinessPlan = props.plans.find((plan) => {
        return plan.type === BYOK_BUSINESS
    })

    return (
        <section className="section padding-bottom">
            <div className="container-xl">
                <div className="w-layout-grid team-pricing" style={{display: 'grid', gridAutoRows: '1fr'}}>
                    {/*<FreeTrialPlanEntry/>*/}
                    {
                        normalPlans.map(plan => {
                            return (
                                <PlanEntry planData={{
                                    plan: plan
                                }} key={plan.id} cancelled={props.cancelled}
                                           testMode={props.testMode}/>
                            )
                        })
                    }
                    <BYOKPersonalPlanEntry/>
                    {
                        byokBusinessPlan && (
                            <BYOKBusinessPlanEntry plan={byokBusinessPlan} cancelled={props.cancelled}
                                                   testMode={props.testMode}/>
                        )
                    }
                    <EnterprisePlanEntry/>
                </div>
            </div>
        </section>
    )
}

function CreditPurchaseList(props: { creditPurchases: CreditPurchase[], testMode: boolean }) {
    return (
        <section className="section padding-bottom">
            <div className="container-xl">
                <div className="w-layout-grid team-pricing" style={{display: 'grid', gridAutoRows: '1fr'}}>
                    {
                        props.creditPurchases.map(creditPurchase => {
                            const planData: CreditPurchaseData = {
                                creditPurchase: creditPurchase
                            }
                            return (
                                <CreditPurchaseEntry creditPurchaseData={planData} testMode={props.testMode}
                                                     key={creditPurchase.id}/>
                            )
                        })
                    }
                </div>
            </div>
        </section>
    )
}

const pricingCellStyle: React.CSSProperties = {padding: "10px 12px", borderBottom: "1px solid rgba(255,255,255,0.08)", textAlign: "right", whiteSpace: "nowrap"}
const pricingHeadStyle: React.CSSProperties = {...pricingCellStyle, fontWeight: 600, opacity: 0.8, whiteSpace: "normal", verticalAlign: "bottom", borderBottom: "1px solid rgba(255,255,255,0.2)"}
const formulaBoxStyle: React.CSSProperties = {border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "10px 16px", textAlign: "center", minWidth: 150}

const creditsLabel = (n: number) => `${n} ${n === 1 ? "credit" : "credits"}`

function ModelPricingRow(props: { model: PricingModel }) {
    const m = props.model
    return <tr>
        <td style={{...pricingCellStyle, textAlign: "left", fontWeight: 600}}>{m.name}</td>
        <td style={pricingCellStyle}>≈ {creditsLabel(m.typicalCredits30k)}</td>
        <td style={pricingCellStyle}>≈ {creditsLabel(m.typicalCredits120k)}</td>
        <td style={pricingCellStyle}>{creditsLabel(m.creditsPer100k)}</td>
    </tr>;
}

function ModelSection(props: { name: string, price: string }) {
    return <>
        <div className="line-static"></div>
        <div className="w-layout-grid pricing-row">
            <div className="pricing-cell-1st">
                <div>{props.name}</div>
            </div>
            <div className="pricing-cell">
                <div>{props.price}</div>
            </div>
        </div>
    </>;
}

interface PublicModel {
    name: string
    creditCost: number
}

// The model list is read live from /api/v1/models (public AiScriptConfig records at the current plugin API
// version) rather than hardcoded here. A hardcoded list silently falsifies this page every time the lineup
// changes, which is exactly what had happened by July 2026 — 7 of the 10 models listed no longer existed.
function useLiveModels(): PublicModel[] {
    const [models, setModels] = useState<PublicModel[]>([])

    useEffect(() => {
        let cancelled = false
        fetch("/api/v1/models")
            .then(response => response.json())
            .then((data: { models?: PublicModel[] }) => {
                if (!cancelled && Array.isArray(data.models)) {
                    setModels(data.models)
                }
            })
            .catch(e => console.error(e))
        return () => {
            cancelled = true
        }
    }, [])

    return models
}

export function HowDoCreditsWorkSection(props: { testMode?: boolean }) {
    const [pricing, setPricing] = useState<PricingResponse | null>(null)
    const [failed, setFailed] = useState<boolean>(false)
    const prefix = props.testMode ? "test/" : ""
    // Flat pricing (or a backend without /pricing): show the live per-request credit list, as before.
    const models = useLiveModels()

    useEffect(() => {
        let cancelled = false
        setFailed(false)
        fetch(`/api/v1/${prefix}pricing`)
            .then((response) => {
                if (!response.ok) {
                    throw new Error("pricing request failed: " + response.status)
                }
                return response.json()
            })
            .then((json: PricingResponse) => {
                if (!cancelled) {
                    setPricing(json)
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setFailed(true)
                }
            })
        return () => {
            cancelled = true
        }
    }, [prefix])

    const showCostPlus = !failed && pricing !== null && pricing.mode === "costplus" && Array.isArray(pricing.models)

    return (
        <div className="container-lg" id="credits-explanation">
            <div className="max-width-xl">
                <div className="margin-bottom margin-xl">
                    <div className="w-layout-grid caption-holder">
                        <div className="line-holder">
                            <div
                                className="line-animated"></div>
                        </div>
                    </div>
                </div>
                <div className="w-layout-grid row-md">
                    <div>
                        <div className="margin-bottom margin-base">
                            <h2 className="title-sm _20ch">How pricing works</h2>
                        </div>
                    </div>
                </div>
            </div>
            {
                showCostPlus && pricing ? (
                    <>
                        <div className="text-lg _48ch">Each request is charged for the AI work it uses: no flat
                            per-request price. Small questions cost a few credits; big multi-file refactors cost
                            more. The model list shows each model's typical cost, expensive requests ask before
                            sending, and after each request you see exactly what was charged.<br/></div>
                        <div style={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, margin: "20px 0"}}>
                            <div style={formulaBoxStyle}>AI work used<br/><b>tokens × model rate</b></div>
                            <span style={{fontSize: 22, opacity: 0.6}}>→</span>
                            <div style={formulaBoxStyle}>rounded up<br/><b>whole credits</b></div>
                            <span style={{fontSize: 22, opacity: 0.6}}>·</span>
                            <div style={formulaBoxStyle}>failed requests<br/><b>only AI work done</b></div>
                        </div>
                        <div style={{overflowX: "auto", width: "100%", marginBottom: 20}}>
                            <table style={{width: "100%", borderCollapse: "collapse"}}>
                                <thead>
                                <tr>
                                    <th style={{...pricingHeadStyle, textAlign: "left"}}>Model</th>
                                    <th style={pricingHeadStyle}>Typical request, 30k context</th>
                                    <th style={pricingHeadStyle}>Typical request, 120k context</th>
                                    <th style={pricingHeadStyle}>≈ per 100k context</th>
                                </tr>
                                </thead>
                                <tbody>
                                {
                                    pricing.models.map((model) => (
                                        <ModelPricingRow key={model.type} model={model}/>
                                    ))
                                }
                                </tbody>
                            </table>
                        </div>
                        <div className="text-base _48ch">Plans and credit packs are priced in dollars and grant
                            credits as shown on each card. Every request is charged in credits for the AI work it
                            uses, rounded up, with a minimum of {pricing.minCharge} credit
                            {pricing.minCharge === 1 ? "" : "s"}. If a request fails, you are charged only for the
                            AI work already done before the failure. &quot;Typical&quot; figures are medians of
                            real Codebuddy requests; your requests will vary with how much context and output they
                            use. Using your own API key (BYOK)? Requests covered by your key cost 0 credits.
                        </div>
                    </>
                ) : (
                    <>
                <div className="text-lg _48ch">Credits are the currency used on Codebuddy to make requests to
                    different AI models.<br/> Each time you ask Codebuddy to do something, it costs a certain number
                    of credits <br/>depending on the model you're using for the main portion of the
                    request.<br/></div>
                <div className="pricing-table">
                    {
                        models.map(model => (
                            <ModelSection key={model.name} name={model.name}
                                          price={`${model.creditCost} Credits / request`}/>
                        ))
                    }
                </div>
                    </>
                )
            }
        </div>
    )
}

export function CreditInfo() {
    const jumpToCreditsExplanation = () => {
        scrollToId("credits-explanation")
    }
    return (
        <span onClick={jumpToCreditsExplanation} className={"cursor-pointer"}>
            <i className={"fas fa-info-circle"}/>
        </span>
    )
}